
import {IUserLogEntryBuilder} from './i.user.log.entry.builder';
import {IUserLogEntryOptions} from './i.user.log.entry.options';
import {UserLogEntryHelper}   from './user.log.entry.helper';

/**
 *
 * @class OptimizationBuilder
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2021
 *
 */
export class OptimizationBuilder implements IUserLogEntryBuilder {

  /** convert raw MongoDB user log entry into data that can construct a tree node */
  build(data: any, args: IUserLogEntryOptions): any {

    const u: string = data._id.match(/^sel-(.*)/i)[1];

    /*
     * we have to construct the children from 3 implicit sources:
     *
     *   - the info messages
     *   - the accepted authorities
     *   - the rejected authorities
     *
     */

    let accepted: any[]  = [];
    let rejected: any[]  = [];
    let messages: any[]  = [];
    let order: number    = 0;

    /* first fake out message children */

    data.i.forEach((text: string, index) => {

      messages.push({
        id:          `msg-${data._id}--msg--${index}`,
        _id:         `msg-${data._id}--msg--${index}`,
        index,
        seq:         index,
        name:        text,
        hasChildren: false,
        isExpanded:  false,
        type:        'message',
        children:    [],
        kids:        [],
        tags:        [],
        label:       'msg',
        lb:          'msg',
        pkey:        data._id,
        pa:          data._id,
        text,
        tx:          text,
        u:           index
      });

      order = index;

    });

    /* now the accepted authorities */

    data.a.forEach((accept: any, index: number) => {

      const parsed: any = UserLogEntryHelper.parseAuthority(accept);

      let title: string = `Authority: `;

      /*
       * include the first authority in the title so they can get a sense of the
       * direction the train is moving in.
       *
       */

      if (parsed.authorities.length > 0) {

        const a = parsed.authorities[0];

        if (a.route != null) {

          title += `RT: ${a.route.id} `;
        }

        title += `${a.track.desc} TRK: (${a.track.id})`;
      }

      /* build the node! */

      accepted.push({
        id:                 `${data._id}--accepted-${index}`,
        index:              index,
        name:               title,
        hasChildren:        false,
        isExpanded:         false,
        type:               'accepted-selections',
        children:           [],
        kids:               [],
        tags:               [],
        label:              'accepted',
        pkey:               `${data._id}--accepted-holder`,
        train:              parsed.train,
        follower:           parsed.follower,
        reason:             parsed.reason,
        proposedMoveExited: parsed.proposedMoveExited,
        authByFWD:          parsed.authByFWD,
        authorities:        parsed.authorities,
        opposingTrains:     parsed.opposingTrains,
        text:               title,
        u:                  `${data._id}--accepted-${index}`
      });

    });

    order++;
    const acceptedHolder: any = {
      id:          `${data._id}--accepted-holder`,
      index:       order,
      name:        `Accepted Authorities`,
      hasChildren: (accepted.length > 0),
      isExpanded:  false,
      type:        'accepted-authorities',
      children:    accepted,
      kids:        [],
      tags:        [],
      label:       'accepted-holder',
      pkey:        data._id,
      text:        `Accepted Authorities`,
      u:           `${data._id}--accepted-holder`
    };

    /* now the rejected authorities */

    data.r.forEach((reject: any, index: number) => {

      const parsed: any = UserLogEntryHelper.parseAuthority(reject);

      let title: string = `Authority: `;

      /*
       * include the first authority in the title so they can get a sense of the
       * direction the train is moving in.
       *
       */

      if (parsed.authorities.length > 0) {

        const a = parsed.authorities[0];

        if (a.route != null) {

          title += `RT: ${a.route.id} `;
        }

        title += `${a.track.desc} TRK: (${a.track.id})`;
      }

      /* build the node! */

      rejected.push({
        id:                 `${data._id}--rejected-${index}`,
        index:              index,
        name:               title,
        hasChildren:        false,
        isExpanded:         false,
        type:               'rejected',
        children:           [],
        kids:               [],
        tags:               [],
        label:              'rejected-selections',
        pkey:              `${data._id}--rejected-holder`,
        train:              parsed.train,
        follower:           parsed.follower,
        reason:             parsed.reason,
        proposedMoveExited: parsed.proposedMoveExited,
        authByFWD:          parsed.authByFWD,
        authorities:        parsed.authorities,
        opposingTrains:     parsed.opposingTrains,
        text:               title,
        u:                  `${data._id}--rejected-${index}`
      });

    });

    order++;
    const rejectedHolder: any = {
      id:          `${data._id}--rejected-holder`,
      index:       order,
      name:        `Rejected Authorities`,
      hasChildren: (rejected.length > 0),
      isExpanded:  false,
      type:        'rejected-authorities',
      children:    rejected,
      kids:        [],
      tags:        [],
      label:       'rejected-holder',
      pkey:        data._id,
      text:        `Rejected Authorities`,
      u:           `${data._id}--rejected-holder`
    };

    return {
      id:          data._id,
      seq:         parseInt(data.seq),
      index:       args.index,
      name:        `Selection`,
      hasChildren: true,
      isExpanded:  false,
      type:        'selection',
      children:    [...messages, acceptedHolder, rejectedHolder],
      parent:      args.parent,
      kids:        [],
      tags:        data.tg,
      label:       data.lb,
      pkey:        data.pa,
      u
    };

  }
}
